import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@xantory/store';
import { api } from '../../core/client';
import { resolvePath } from '../../core/path';
import type {
  BatchReceiveReport,
  SiteAssetModelOption,
  SiteSupplierOption,
  ProvisionalModelBody,
  AssetAttributeDef,
  AssignCustodyBody,
  AssetClassOption,
  AssetImportReport,
  AssetKpisRecord,
  AssetRelationKind,
  AssetSoftwareBody,
  AssetSoftwareRecord,
  AssetLabel,
  AssetLabelBatch,
  AssetLabelStats,
  AssetMovement,
  AssetState,
  AssetSubstatus,
  AssetTransitionBody,
  DiscoveredDevice,
  LabelSequencePreview,
  MovementFacets,
  MovementReason,
  NewLabelBatchBody,
  OpStatus,
  RefrigerantLogBody,
  SiteAsset,
  SiteAssetBody,
  SiteAssetDetail,
} from './site-assets.types';

const BASE = '/v1/assets';
const KEY = ['site-assets'] as const;

export interface SiteAssetFilters {
  state?: AssetState | '' | undefined;
  class?: string | undefined;
  location?: string | undefined;
  q?: string | undefined;
  /** R-235: `office` | `farm`. Eligibility, not geography — resolved server-side
   *  through the model's domains, falling back to the class's. */
  domain?: string | undefined;
}

/** The one place the filter query string is built, so the list and the CSV export
 *  cannot ask for different rows. */
function assetQuery(filters: SiteAssetFilters): string {
  const qs = new URLSearchParams();
  if (filters.state) qs.set('state', filters.state);
  if (filters.class) qs.set('class', filters.class);
  if (filters.location) qs.set('location', filters.location);
  if (filters.q) qs.set('q', filters.q);
  if (filters.domain) qs.set('domain', filters.domain);
  return qs.toString();
}

export function useSiteAssets(filters: SiteAssetFilters = {}) {
  const s = assetQuery(filters);
  return useQuery({
    queryKey: [...KEY, filters],
    queryFn: () => api.get<SiteAsset[]>(`${BASE}${s ? `?${s}` : ''}`),
  });
}

/** Detail by UUID or asset tag (QR scans carry the tag). */
export function useSiteAsset(idOrTag: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', idOrTag],
    queryFn: () => api.get<SiteAssetDetail>(`${BASE}/${idOrTag}`),
    enabled: !!idOrTag,
  });
}

/** Per-asset KPIs vs the fleet class average (R-130 Performance tab). */
export function useAssetKpis(idOrTag: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'kpis', idOrTag],
    queryFn: () => api.get<AssetKpisRecord>(`${BASE}/${idOrTag}/kpis`),
    enabled: !!idOrTag,
  });
}

export function useAssetClasses() {
  return useQuery({
    queryKey: [...KEY, 'classes'],
    queryFn: () => api.get<AssetClassOption[]>(`${BASE}/classes`),
    staleTime: 5 * 60_000,
  });
}

/** Assignment-group options (replica) — the managed_by_group picker (R-141). */
export interface AssetGroupOption {
  id: string;
  key: string;
  name: string;
}
export function useAssetGroups() {
  return useQuery({
    queryKey: ['site-assets', 'groups'],
    queryFn: () => api.get<AssetGroupOption[]>('/v1/assets/groups'),
    staleTime: 300_000,
  });
}

export function useAssetAttributeDefs(classCode: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'defs', classCode],
    queryFn: () => api.get<AssetAttributeDef[]>(`${BASE}/attribute-defs?class=${classCode}`),
    enabled: !!classCode,
    staleTime: 5 * 60_000,
  });
}

export function useCreateSiteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SiteAssetBody) => api.post<SiteAsset>(BASE, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Bulk-import assets from a CSV (E4). Dry-run validates without creating;
 *  a real run mints + mirrors each row and invalidates the register. */
export function useImportAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ csv, dryRun }: { csv: string; dryRun: boolean }) =>
      api.post<AssetImportReport>(`${BASE}/import?dry_run=${dryRun}`, csv, {
        headers: { 'Content-Type': 'text/csv' },
      }),
    onSuccess: (_r, vars) => {
      if (!vars.dryRun) qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateSiteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SiteAssetBody }) =>
      api.put<SiteAsset>(`${BASE}/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** One handover-checklist line (A-7, handbook K.6) — a required gate when an
 *  IT / `requires_sanitisation` class is deployed to a PERSON custodian: the
 *  backend 400s unless EVERY line is checked. */
export interface HandoverChecklistItem {
  item: string;
  checked: boolean;
}

/** R-235: THE move — the prototype's Move dialog.
 *
 *  Every change of state or placement goes through here, and the server appends
 *  the ledger row in the same transaction. `reason` is one of `MANUAL_REASONS`;
 *  omit it and the server takes the one the pressed button implies. A move to
 *  `in_use`/`in_transit` MUST carry `location_code` — the server refuses it up
 *  front rather than failing on the placement trigger, so the operator gets "pick
 *  the location" and not an error.
 *
 *  The evidence gates still apply and are deliberately NOT in the prototype:
 *  a photo before service, a signature + checklist for a person handover, a
 *  sanitisation certificate before retiring a data-bearing class, and a disposal
 *  certificate signed off by someone other than whoever retired it. */
export function useMoveSiteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & AssetTransitionBody) =>
      // Undefined keys are dropped rather than sent as null: the server reads an
      // absent placement as "keep what it has", and a null as "clear it".
      api.post<SiteAsset>(
        `${BASE}/${id}/transition`,
        Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined)),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** R-235 — the prototype's Assign dialog. Custody only: nothing here can move the
 *  asset, which is what keeps the Move dialog the single writer of placement.
 *
 *  ⚠ Sends all four fields every time, nulls included — the endpoint is a full
 *  replace and a blank means CLEAR. Filtering undefined out (as the move does)
 *  would make unassigning impossible. */
export function useAssignAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & AssignCustodyBody) =>
      api.post<SiteAsset>(`${BASE}/${id}/assign`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** @deprecated R-235 renamed this to `useMoveSiteAsset`, because it is a MOVE now
 *  — it carries a reason and writes the ledger. Kept as a thin alias for one
 *  release so the screens can migrate one at a time. */
export function useTransitionSiteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      state,
      substatus,
      assigned_to,
      location_code,
      checklist,
      retirement_reason,
      disposal_method,
      disposal_vendor,
      disposal_date,
    }: {
      id: string;
      state: AssetState;
      substatus?: AssetSubstatus;
      assigned_to?: string | null;
      location_code?: string | null;
      /** A-7 handover checklist (deploy to a person of an IT class). */
      checklist?: HandoverChecklistItem[];
      /** K-9 — optional free-text reason carried on the `retired` transition. */
      retirement_reason?: string;
      /** K-10 — disposal fields carried on the `disposed` transition. */
      disposal_method?: string;
      disposal_vendor?: string;
      disposal_date?: string;
    }) =>
      api.post<SiteAsset>(`${BASE}/${id}/transition`, {
        state,
        ...(substatus ? { substatus } : {}),
        ...(assigned_to !== undefined ? { assigned_to } : {}),
        ...(location_code !== undefined ? { location_code } : {}),
        ...(checklist ? { checklist } : {}),
        ...(retirement_reason ? { retirement_reason } : {}),
        ...(disposal_method ? { disposal_method } : {}),
        ...(disposal_vendor ? { disposal_vendor } : {}),
        ...(disposal_date ? { disposal_date } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSetSiteAssetOpStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, op_status }: { id: string; op_status: OpStatus }) =>
      api.post<SiteAsset>(`${BASE}/${id}/op-status`, { op_status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAddAssetRelation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, to_id, rel }: { id: string; to_id: string; rel: AssetRelationKind }) =>
      api.post<boolean>(`${BASE}/${id}/relations`, { to_id, rel }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRemoveAssetRelation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, to_id, rel }: { id: string; to_id: string; rel: AssetRelationKind }) =>
      api.delete<boolean>(`${BASE}/${id}/relations`, { body: { to_id, rel } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Raw-body document upload (R-123): the file bytes with their Content-Type. */
export function useUploadAssetDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, kind, file }: { id: string; kind: string; file: File }) => {
      const { accessToken } = useAuthStore.getState();
      const url = resolvePath(`${BASE}/${id}/docs?kind=${kind}&name=${encodeURIComponent(file.name)}`);
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: file,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `upload failed (${res.status})`);
      }
      return (await res.json()) as { data: SiteAsset };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Remove one register entry by index (the stored object is retained). */
export function useRemoveAssetDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, index }: { id: string; index: number }) =>
      api.delete<SiteAsset>(`${BASE}/${id}/docs/${index}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Authorized binary download of one document; opens it in a new tab. */
export async function openAssetDoc(id: string, index: number): Promise<void> {
  const { accessToken } = useAuthStore.getState();
  const res = await fetch(resolvePath(`${BASE}/${id}/docs/${index}`), {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) throw new Error(`download failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ─── software register (C-2, R-147) ─────────────────────────────────────────

export function useAssetSoftware(id: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'software', id],
    queryFn: () => api.get<AssetSoftwareRecord[]>(`${BASE}/${id}/software`),
    enabled: !!id,
  });
}

export function useAddAssetSoftware() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AssetSoftwareBody }) =>
      api.post<AssetSoftwareRecord>(`${BASE}/${id}/software`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRemoveAssetSoftware() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, sid }: { id: string; sid: string }) =>
      api.delete<boolean>(`${BASE}/${id}/software/${sid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ─── refrigerant log (A-5, R-147) ───────────────────────────────────────────

/** POST a refrigerant handling entry; it lands in the asset event history. */
export function useAddRefrigerantLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RefrigerantLogBody }) =>
      api.post<boolean>(`${BASE}/${id}/refrigerant-log`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useNextFromStock(classCode: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'stock-next', classCode],
    queryFn: () => api.get<SiteAsset | null>(`${BASE}/stock/next?class=${classCode}`),
    enabled: !!classCode,
  });
}

// ── asset models (R-165 / D-120) ──────────────────────────────────────────────

/**
 * Cloud catalogue ∪ this site's provisionals. Works with no uplink.
 *
 * R-245 — `classCode` narrows to one class, server-side and EXACTLY (never by
 * prefix: a category is not a leaf). Pass it wherever the class is already
 * settled and the register would refuse anything else, so the picker cannot
 * offer a product the create is going to reject. Omit it for the plain
 * catalogue, which is what every other caller wants.
 *
 * ⚠ The class is part of the query key: two classes are two cached lists, not
 * one list that flickers between scopes.
 */
export function useSiteAssetModels(classCode?: string | null) {
  const scoped = classCode?.trim() || null;
  return useQuery({
    queryKey: [...KEY, 'models', scoped],
    queryFn: () =>
      api.get<SiteAssetModelOption[]>(
        scoped ? `${BASE}/models?class_code=${encodeURIComponent(scoped)}` : `${BASE}/models`,
      ),
    staleTime: 5 * 60_000,
  });
}

/** Supplier master, mirrored to the site so the picker survives an uplink
 *  outage. Includes unapproved suppliers — `approved` limits who may be
 *  purchased from, not who a unit was bought from. */
export function useSiteSuppliers() {
  return useQuery({
    queryKey: [...KEY, 'suppliers'],
    queryFn: () => api.get<SiteSupplierOption[]>(`${BASE}/suppliers`),
    staleTime: 5 * 60_000,
  });
}

/** R-165 P4: receive N units of one product in a single action. */
export function useBatchReceive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SiteAssetBody & { serials: string[] }) =>
      api.post<BatchReceiveReport>(`${BASE}/batch`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Author a model at the site — the offline path. Usable immediately; the cloud
 *  promotes it when the link is up. */
export function useCreateProvisionalModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProvisionalModelBody) => api.post<SiteAssetModelOption>(`${BASE}/models`, body),
    // Prefix match, so R-245's per-class lists are invalidated along with the
    // unscoped one — a product authored at intake has to appear in the very
    // class-scoped picker the operator opened the modal from.
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'models'] }),
  });
}

// ─── R-235: the movement ledger ──────────────────────────────────────────────

export interface MovementFilters {
  /** One asset's history — what the record's Movement-history tab asks for. */
  asset_id?: string | undefined;
  reason?: MovementReason | '' | undefined;
  moved_by?: string | undefined;
  /** The prototype's "In transit only" checkbox is `to_state=in_transit`. */
  to_state?: AssetState | '' | undefined;
  /** "What did this change / receipt actually move." */
  linked_record_type?: string | undefined;
  linked_record_id?: string | undefined;
  limit?: number | undefined;
}

/** The append-only ledger, newest first.
 *
 *  There is no write counterpart and there never will be: rows are appended by
 *  the backend inside the transaction that performs the move, so a client cannot
 *  record a move that did not happen — nor fail to record one that did. */
export function useAssetMovements(filters: MovementFilters = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) if (v) qs.set(k, String(v));
  const s = qs.toString();
  return useQuery({
    queryKey: [...KEY, 'movements', filters],
    queryFn: () => api.get<AssetMovement[]>(`${BASE}/movements${s ? `?${s}` : ''}`),
  });
}

/** The Transfers screen's two filter dropdowns, built from the rows that exist so
 *  a filter never offers a value matching nothing. */
export function useMovementFacets() {
  return useQuery({
    queryKey: [...KEY, 'movement-facets'],
    queryFn: () => api.get<MovementFacets>(`${BASE}/movements/facets`),
    staleTime: 60_000,
  });
}

/** R-235: the register as CSV, filtered exactly like the list it mirrors.
 *
 *  Server-side on purpose — the file is then the rows RBAC allowed and the filters
 *  asked for, rather than whatever this client happened to have paged in. The
 *  download is a plain navigation so the browser handles the save dialog. */
export function assetsExportUrl(filters: SiteAssetFilters = {}): string {
  const s = assetQuery(filters);
  return resolvePath(`${BASE}/export.csv${s ? `?${s}` : ''}`);
}

// ─── R-235: the Unregistered-device quarantine ───────────────────────────────

/** Devices seen on the network that no asset accounts for.
 *
 *  `pending` needs the site's device inventory, so it is the one tab that can fail
 *  when iot-manager is unreachable — and it fails loudly rather than showing an
 *  empty queue, because "nothing to do" is the most misleading answer available.
 *
 *  Nothing here creates an asset. That is the whole point of the screen: ingest
 *  creates the DEVICE record and a human decides what it is. */
export function useDiscoveredDevices(status: 'pending' | 'bound' | 'rejected' = 'pending') {
  return useQuery({
    queryKey: [...KEY, 'unregistered', status],
    queryFn: () => api.get<DiscoveredDevice[]>(`${BASE}/unregistered?status=${status}`),
  });
}

/** R-235 — the pending count for the Unregistered nav badge (the prototype's
 *  `nc-unreg`).
 *
 *  ⚠ A DEDICATED COUNT, not `useDiscoveredDevices().data.length`, and the reason is
 *  cost: the list runs a match-suggestion query per unclaimed device, which the
 *  badge needs none of. This is on the path of every page load for anyone who can
 *  read assets, so it stays a set difference.
 *
 *  `enabled` is not optional in practice — the caller must gate it on the assets
 *  permission AND on a site being selected, or it fires during the cloud console's
 *  no-site window and caches an error under a key that never changes. */
export function useUnregisteredCount(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'unregistered', 'count'],
    queryFn: () => api.get<number>(`${BASE}/unregistered/count`),
    // A device appearing on the network is not a per-second event, and this rides
    // every page load. Two minutes keeps the badge honest without polling hard.
    staleTime: 120_000,
    refetchInterval: 120_000,
    enabled,
  });
}

export function useBindDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      device_ref,
      asset_id,
      match_rule,
    }: {
      device_ref: string;
      asset_id: string;
      match_rule?: string;
    }) =>
      api.post<boolean>(`${BASE}/unregistered/${encodeURIComponent(device_ref)}/bind`, {
        asset_id,
        ...(match_rule ? { match_rule } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Reject a device. The note is REQUIRED — the prototype's own example is "a test
 *  rig someone left on the network", and six months later a bare rejection is
 *  indistinguishable from a mistake. */
export function useRejectDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ device_ref, note }: { device_ref: string; note: string }) =>
      api.post<boolean>(`${BASE}/unregistered/${encodeURIComponent(device_ref)}/reject`, { note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** "Move back to pending" — a delete of the rejection, which is what pending IS. */
export function useReopenDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (device_ref: string) =>
      api.delete<boolean>(`${BASE}/unregistered/${encodeURIComponent(device_ref)}/rejection`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Unbind, keyed on the ASSET — the direction the record's telemetry tab asks
 *  from, and the direction that matters: the asset is the master, so dropping its
 *  device link leaves the asset untouched and never the other way round. */
export function useUnbindDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (asset_id: string) => api.delete<boolean>(`${BASE}/unregistered/asset/${asset_id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ─── R-235: the QR label pool ────────────────────────────────────────────────

const LABELS = '/v1/assets/labels';
const LKEY = ['asset-labels'] as const;

export interface LabelFilters {
  class_code?: string | undefined;
  state?: 'unused' | 'bound' | 'void' | '' | undefined;
  batch_id?: string | undefined;
}

export function useAssetLabels(filters: LabelFilters = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) if (v) qs.set(k, String(v));
  const s = qs.toString();
  return useQuery({
    queryKey: [...LKEY, filters],
    queryFn: () => api.get<AssetLabel[]>(`${LABELS}${s ? `?${s}` : ''}`),
  });
}

export function useAssetLabelBatches(classCode?: string) {
  return useQuery({
    queryKey: [...LKEY, 'batches', classCode ?? null],
    queryFn: () =>
      api.get<AssetLabelBatch[]>(
        `${LABELS}/batches${classCode ? `?class_code=${encodeURIComponent(classCode)}` : ''}`,
      ),
  });
}

/** Per-class pool counts — what the top level of the drill-down renders, and what
 *  tells an operator the pool is empty BEFORE they walk to the dock. */
export function useAssetLabelStats() {
  return useQuery({
    queryKey: [...LKEY, 'stats'],
    queryFn: () => api.get<AssetLabelStats[]>(`${LABELS}/stats`),
  });
}

/** The next numbers, without minting them. Safe to call as the operator types:
 *  it reads the counter rather than allocating from it, so two people previewing
 *  at once see the same answer and neither consumes anything. */
export function useLabelSequencePreview(classCode: string | null, count: number) {
  return useQuery({
    queryKey: [...LKEY, 'preview', classCode, count],
    queryFn: () =>
      api.get<LabelSequencePreview>(
        `${LABELS}/next-sequence?class_code=${encodeURIComponent(classCode ?? '')}&count=${count}`,
      ),
    enabled: !!classCode && count > 0 && count <= 500,
  });
}

export function useGenerateLabelBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NewLabelBatchBody) =>
      api.post<{ batch: AssetLabelBatch; labels: AssetLabel[] }>(`${LABELS}/batches`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: LKEY }),
  });
}

/** Void an UNUSED label. A BOUND one is refused by the server: it is the only link
 *  between a sticker and a unit, and breaking it would leave a tag on a shelf the
 *  register cannot explain. */
export function useVoidLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<AssetLabel>(`${LABELS}/${id}/void`),
    onSuccess: () => qc.invalidateQueries({ queryKey: LKEY }),
  });
}
